/**
 * ManualEntryPreflight — 4-step wizard for the consolidated manual entry
 * flow (#1863). Modeled exactly after the legacy ManualCallEntry wizard
 * structure (Time Sheet tab) so reps see the same mental model.
 *
 * Steps:
 *   1. Date — timeline-aware. Mode (live-takeover vs retroactive) is
 *      INFERRED from the date: today = live, past = retroactive. A banner
 *      confirms the mode; no separate question.
 *   2. Where — bidirectional project + store typeahead picker. Either side
 *      can be picked first; the other cross-filters.
 *   3. Details — Drive to Store (time + odometer together), Store Time
 *      (time in/out), Last visit of day? Y/N (explicit), Drive Home (if
 *      yes), Team Project / Team Leader Y/N, Optional reason, Notes.
 *   4. Confirm — summary card, back button, submit creates CallLog +
 *      navigates to canonical StoreDetail visit flow.
 *
 * After submit:
 *   - CallLog created server-side with stcalltime + drive odo + (optionally)
 *     drive times and drive-back
 *   - ManualVisitContext hydrated so StoreDetail renders the canonical
 *     workflow with injected timestamps via useVisitClock
 *
 * Drive-back handling:
 *   - Live mode (today): drive-back is NOT collected here. The canonical
 *     end-day patchReturnDrive flow on Today.tsx will write drive-back
 *     when the rep actually ends their day.
 *   - Retroactive mode (past date): drive-back is collected when the rep
 *     marks "this was my last visit of the day."
 *
 * GPS:
 *   - NOT collected here. The canonical GPSCheckIn in StoreDetail captures
 *     live position at the rep's Confirm Arrival step. If they're at the
 *     store (live takeover) it validates; if not (retroactive from home)
 *     it prompts for an override reason. One code path for both.
 */

import { useState, useEffect, useMemo, useCallback, useRef, Component, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useManualVisit } from '../context/ManualVisitContext';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import { isImplausibleDrive } from '../utils/dayLegs';
import { useOdometerContext } from '../context/OdometerContext';
import {
  callLogsApi,
  projectsApi,
  storesApi,
  type DayTimelineEntry,
  type DayTimelineResponse,
} from '../services/api';
import { formatDateToLocal } from '../utils/timesheetRules';
import { findTimelineOverlap } from '../utils/timelineOverlap';

type WizardStep = 1 | 2 | 3 | 4;
type UseCase = 'live-takeover' | 'retroactive';

interface StoreResult {
  id: number;
  legacystoreid: number;
  name: string;
  address: string;
  retailer_abbreviation: string;
  storenumber: string;
}

interface ProjectResult {
  projectid: number;
  projectdesc: string;
}

/** Off-schedule reason categories (optional). Mirrors the legacy
 * ManualCallEntry's unassigned-reason list so reps see familiar choices.
 * Persisted by prefixing the CallLog's notes field — there's no dedicated
 * server column today. */
const OFF_SCHEDULE_REASONS = [
  { value: 'coverage', label: 'Coverage for absent colleague' },
  { value: 'training', label: 'Training / Ride-along' },
  { value: 'emergency', label: 'Emergency coverage' },
  { value: 'requested', label: 'Store/Manager requested visit' },
  { value: 'route_change', label: 'Route change pending update' },
  { value: 'device_failure', label: 'Device failed onsite (after-the-fact)' },
  { value: 'other', label: 'Other (specify below)' },
] as const;
type ReasonValue = (typeof OFF_SCHEDULE_REASONS)[number]['value'];

// ============================================================================
// Helpers
// ============================================================================

function buildIsoTimestamp(date: string, time: string): string {
  return `${date}T${time}:00`;
}

function timeToMinutes(time: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const mi = parseInt(m[2], 10);
  if (h < 0 || h > 23 || mi < 0 || mi > 59) return null;
  return h * 60 + mi;
}

function dateToHHMM(d: Date): string {
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

function formatTimeRange(start: string, end: string | null): string {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  if (!end) return `${fmt(start)} — in progress`;
  return `${fmt(start)} – ${fmt(end)}`;
}

function formatDurationHM(minutes: number): { text: string; isOvernight: boolean } {
  const isOvernight = minutes < 0;
  const m = isOvernight ? minutes + 24 * 60 : minutes;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return { text: `${h}h ${mm}m`, isOvernight };
}

function readAssociateId(): number | null {
  try {
    const raw = localStorage.getItem('associate');
    if (raw) {
      const a = JSON.parse(raw);
      const id = a.associateid ?? a.id;
      if (typeof id === 'number') return id;
    }
    const impRaw = localStorage.getItem('impersonating');
    if (impRaw) {
      const a = JSON.parse(impRaw);
      const id = a.associateid ?? a.id;
      if (typeof id === 'number') return id;
    }
  } catch {
    // fall through
  }
  return null;
}

interface OverlapResult {
  conflict: DayTimelineEntry | null;
  message: string | null;
}

function deriveDefaults(entries: DayTimelineEntry[]): { timeIn: string; startOdo: string } {
  if (entries.length === 0) {
    return { timeIn: '08:00', startOdo: '' };
  }
  const last = entries[entries.length - 1];
  const lastEnd = last.end_time || last.start_time;
  const d = new Date(lastEnd);
  const minutes = d.getMinutes();
  const round15 = Math.ceil(minutes / 15) * 15;
  const adjusted = new Date(d);
  adjusted.setMinutes(round15, 0, 0);
  let lastOdo = '';
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i];
    if (typeof e.end_odometer === 'number' && e.end_odometer > 0) {
      lastOdo = String(e.end_odometer);
      break;
    }
    if (typeof e.start_odometer === 'number' && e.start_odometer > 0) {
      lastOdo = String(e.start_odometer);
      break;
    }
  }
  return { timeIn: dateToHHMM(adjusted), startOdo: lastOdo };
}

// ============================================================================
// Error boundary — surfaces render crashes inline instead of white-screening.
// ============================================================================

interface ErrBoundaryState {
  error: Error | null;
}
class WizardErrorBoundary extends Component<{ children: ReactNode }, ErrBoundaryState> {
  state: ErrBoundaryState = { error: null };
  static getDerivedStateFromError(error: Error): ErrBoundaryState {
    return { error };
  }
  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // Surface to the browser console for fastest debugging.
    console.error('[ManualEntryPreflight] render crash', error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-slate-50 p-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h2 className="mb-2 font-heading text-lg font-semibold text-red-800">
              Manual entry crashed
            </h2>
            <p className="mb-4 text-sm text-red-700">
              The form hit an unexpected error. Open browser DevTools console for the full stack
              trace. The error message is below — please share it with the developer.
            </p>
            <pre className="overflow-auto whitespace-pre-wrap rounded-md bg-white p-3 text-xs text-red-900 ring-1 ring-red-100">
              {this.state.error.message}
              {this.state.error.stack && `\n\n${this.state.error.stack}`}
            </pre>
            <button
              type="button"
              onClick={() => {
                this.setState({ error: null });
                window.location.reload();
              }}
              className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// Component
// ============================================================================

function ManualEntryPreflightInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { manualVisit: existingManualVisit, setManualVisit, clearManualVisit } = useManualVisit();

  // Idempotency key for the createCallLog POST (#1867). Generated once on
  // first mount; re-used across all retries within this pre-flight session.
  // Crypto.randomUUID is available in all modern PWA-supporting browsers; the
  // Math.random fallback is just to keep the type system happy.
  const [idempotencyKey] = useState<string>(() => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch {
      // fall through
    }
    return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  });

  // ---- Wizard state ----
  const [step, setStep] = useState<WizardStep>(1);

  // ---- Step 1: Date + timeline ----
  const today = formatDateToLocal(new Date());
  const [date, setDate] = useState<string>(today);
  const [timeline, setTimeline] = useState<DayTimelineResponse | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);

  const { dayData, clockWindows } = useDayTrackingContext();
  const { odometerData } = useOdometerContext();
  const useCase: UseCase = useMemo(() => (date >= today ? 'live-takeover' : 'retroactive'), [date, today]);

  // The live day already knows what this form was asking the rep to retype. When
  // they are clocked in — and especially mid-drive — the open leg carries when
  // the drive began and the odometer they confirmed at that point, and "now" is
  // the obvious arrival. Re-asking invites a rep to contradict their own device,
  // and something then has to reconcile the two. (#3470, diag #80)
  const liveDrive = useMemo(() => {
    if (useCase !== 'live-takeover') return null;
    const entries = dayData.timeEntries || [];
    const open = [...entries].reverse().find((e) => e.type === 'drive' && !e.endTime);
    if (!open || !open.startTime) return null;
    // Carrying the live day in is only a kindness while the live day is
    // believable. A clock-in left running overnight offered this form a drive of
    // 8h24m at 0 miles as its starting point, and a rep who taps through files
    // it (#3518). Better to ask than to suggest something that cannot be true.
    if (isImplausibleDrive(open)) return null;
    return open;
  }, [useCase, dayData.timeEntries]);

  // Say why the form is empty, rather than leaving the rep wondering where the
  // prefill went on a day the app clearly knows about.
  const staleDriveNotice = useMemo(() => {
    if (useCase !== 'live-takeover') return null;
    const entries = dayData.timeEntries || [];
    const open = [...entries].reverse().find((e) => e.type === 'drive' && !e.endTime);
    return open && isImplausibleDrive(open) ? open : null;
  }, [useCase, dayData.timeEntries]);

  const liveClockIn = useMemo(() => {
    if (useCase !== 'live-takeover') return null;
    const open = (clockWindows || []).find((w) => !w.endTime);
    return open?.startTime ? new Date(open.startTime) : null;
  }, [useCase, clockWindows]);

  // ---- Step 2: Project + Store (bidirectional typeahead) ----
  const [projectSearch, setProjectSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [allProjects, setAllProjects] = useState<ProjectResult[]>([]);
  const [projectsForStore, setProjectsForStore] = useState<ProjectResult[]>([]);
  const [projectStores, setProjectStores] = useState<StoreResult[]>([]);
  const [storeSearchResults, setStoreSearchResults] = useState<StoreResult[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingProjectsForStore, setIsLoadingProjectsForStore] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isSearchingStores, setIsSearchingStores] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreResult | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectResult | null>(null);
  // #2675 — pre-select a project passed from the Today screen's "Always
  // Available" section (?projectId=&projectName=), so tapping a pinned admin
  // project drops the rep straight into Manual Entry with the project already
  // chosen (they only pick the store/time). Applied once on mount.
  const pinnedProjectId = useMemo(() => Number(searchParams.get('projectId')) || null, [searchParams]);
  const pinnedProjectApplied = useRef(false);
  useEffect(() => {
    if (pinnedProjectApplied.current || !pinnedProjectId) return;
    pinnedProjectApplied.current = true;
    const name = searchParams.get('projectName')?.trim() || `Project ${pinnedProjectId}`;
    setSelectedProject({ projectid: pinnedProjectId, projectdesc: name });
    // Populate the visible typeahead text too — otherwise the project box looks
    // empty and the rep re-enters it (the whole point was to skip that). #2675.
    setProjectSearch(`${pinnedProjectId} - ${name}`);
  }, [pinnedProjectId, searchParams]);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const storeDropdownRef = useRef<HTMLDivElement>(null);

  // ---- Step 3: Details ----
  // Drive TO the store
  const [driveTimeStart, setDriveTimeStart] = useState('');
  const [driveTimeEnd, setDriveTimeEnd] = useState('');
  const [startOdo, setStartOdo] = useState('');
  const [endOdo, setEndOdo] = useState('');
  // Store time
  const [timeIn, setTimeIn] = useState('');
  const [timeOut, setTimeOut] = useState('');
  // Last visit of day toggle (explicit; only relevant for retroactive)
  const [isLastVisitOfDay, setIsLastVisitOfDay] = useState<boolean>(false);
  // Drive HOME (only collected when isLastVisitOfDay && retroactive)
  const [driveBackTimeStart, setDriveBackTimeStart] = useState('');
  const [driveBackTimeEnd, setDriveBackTimeEnd] = useState('');
  const [driveBackStartOdo, setDriveBackStartOdo] = useState('');
  const [driveBackEndOdo, setDriveBackEndOdo] = useState('');
  // Team flags
  const [isTeamProject, setIsTeamProject] = useState(false);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  // Optional reason + notes
  const [reason, setReason] = useState<ReasonValue | ''>('');
  const [reasonOther, setReasonOther] = useState('');
  const [notes, setNotes] = useState('');

  // ---- Submission ----
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ==========================================================================
  // Effects
  // ==========================================================================

  // Fetch timeline + smart defaults when date changes. The AbortController
  // actually cancels the transfer (#2117) — the previous cancelled-flag
  // pattern only suppressed setState while the download kept saturating
  // slow links after the rep backed out or changed the date (diag #51).
  useEffect(() => {
    if (!date) return;
    const ac = new AbortController();
    setTimelineLoading(true);
    setTimelineError(null);
    callLogsApi
      .getDayTimeline(date, ac.signal)
      .then((data) => {
        if (ac.signal.aborted) return;
        setTimeline(data);
        const defaults = deriveDefaults(data.entries);
        setTimeIn((prev) => prev || defaults.timeIn);
        setStartOdo((prev) => prev || defaults.startOdo);
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        setTimelineError(err instanceof Error ? err.message : 'Failed to load day timeline');
        setTimeline(null);
      })
      .finally(() => {
        if (ac.signal.aborted) return;
        setTimelineLoading(false);
      });
    return () => {
      ac.abort();
    };
  }, [date]);

  // Carry the live day into the form (#3470). Only fills EMPTY fields, so a rep
  // who has already typed is never overwritten, and only in live-takeover — a
  // retroactive entry has no live state to read and keeps its explicit section.
  useEffect(() => {
    if (!liveDrive) return;
    const start = new Date(liveDrive.startTime);
    setDriveTimeStart((prev) => prev || dateToHHMM(start));
    setDriveTimeEnd((prev) => prev || dateToHHMM(new Date()));
    if (liveDrive.startOdometer != null) {
      setStartOdo((prev) => prev || String(liveDrive.startOdometer));
    }
    if (odometerData.currentReading != null) {
      setEndOdo((prev) => prev || String(odometerData.currentReading));
    }
    // Arriving now — not the 08:00 the timeline falls back to when it has no
    // entries, which is a time the rep was demonstrably not at the store.
    setTimeIn(dateToHHMM(new Date()));
  }, [liveDrive, odometerData.currentReading]);

  // Clocked in but not driving: the clock-in is still a better anchor than 08:00.
  useEffect(() => {
    if (liveDrive || !liveClockIn) return;
    setTimeIn((prev) => (prev && prev !== '08:00' ? prev : dateToHHMM(liveClockIn)));
  }, [liveDrive, liveClockIn]);

  // Load ALL projects (~140KB / ~1000 rows) the FIRST time the rep opens the
  // project picker — NOT on wizard mount (#2117). The diag #51 path (open
  // wizard, set a date, back out) used to download the full list for nothing,
  // starving slow links; lazy + abortable means backing out costs zero bytes.
  // The /api/projects/projects/ endpoint returns a WRAPPED response:
  // [{ status, message, data: Project[] }]. The fields the API actually
  // returns are: projectid, projectdesc (often null), clientname,
  // retailername, distributorname. We fall back to a composed label when
  // projectdesc is null.
  const [projectsRequested, setProjectsRequested] = useState(false);
  useEffect(() => {
    if (!projectsRequested) return;
    const ac = new AbortController();
    setIsLoadingProjects(true);

    interface ApiProject {
      projectid: number;
      projectdesc?: string | null;
      projectname?: string | null;
      clientname?: string | null;
      retailername?: string | null;
    }
    interface Wrapped {
      status?: string;
      message?: string;
      data?: ApiProject[];
    }

    projectsApi
      .getProjects(ac.signal)
      .then((raw) => {
        if (ac.signal.aborted) return;
        // Unwrap the [{status, message, data}] envelope. Defensive: also
        // accept a bare Project[] in case the endpoint shape changes.
        const arr = raw as unknown as Wrapped[] | ApiProject[];
        let projectsArr: ApiProject[] = [];
        if (Array.isArray(arr) && arr.length > 0) {
          const first = arr[0] as Wrapped;
          if (first && typeof first === 'object' && 'data' in first && Array.isArray(first.data)) {
            projectsArr = first.data;
          } else {
            projectsArr = arr as ApiProject[];
          }
        }
        const mapped: ProjectResult[] = projectsArr
          .filter((p) => typeof p.projectid === 'number')
          .map((p) => {
            // Build a meaningful label even when projectdesc is null.
            // Priority: projectdesc > projectname > clientname/retailername > id.
            const composed = [p.clientname, p.retailername].filter(Boolean).join(' / ');
            const desc =
              (p.projectdesc && p.projectdesc.trim()) ||
              (p.projectname && p.projectname.trim()) ||
              composed ||
              `Project ${p.projectid}`;
            return { projectid: p.projectid, projectdesc: desc };
          })
          .sort((a, b) => a.projectid - b.projectid);
        setAllProjects(mapped);
      })
      .catch((err) => {
        if (ac.signal.aborted) return;
        console.warn('[preflight] failed to load all projects', err);
      })
      .finally(() => {
        if (ac.signal.aborted) return;
        setIsLoadingProjects(false);
      });
    return () => {
      ac.abort();
    };
  }, [projectsRequested]);

  // When a project is selected, load its stores for the cross-filter.
  useEffect(() => {
    if (!selectedProject) {
      setProjectStores([]);
      return;
    }
    let cancelled = false;
    setIsLoadingStores(true);
    storesApi
      .getStoresByProject(selectedProject.projectid)
      .then((stores) => {
        if (cancelled) return;
        setProjectStores(stores);
      })
      .catch((err) => {
        console.warn('[preflight] failed to load stores for project', err);
        setProjectStores([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingStores(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProject]);

  // Click-outside closes the dropdowns.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target as Node)) {
        setShowProjectDropdown(false);
      }
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(e.target as Node)) {
        setShowStoreDropdown(false);
      }
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced server-side store search (only when no project selected).
  const storeSearchTimer = useRef<number | null>(null);
  useEffect(() => {
    if (storeSearchTimer.current) {
      window.clearTimeout(storeSearchTimer.current);
    }
    if (selectedProject) {
      setStoreSearchResults([]);
      setIsSearchingStores(false);
      return;
    }
    const q = storeSearch.trim();
    if (!q || selectedStore) {
      setStoreSearchResults([]);
      setIsSearchingStores(false);
      return;
    }
    setIsSearchingStores(true);
    storeSearchTimer.current = window.setTimeout(async () => {
      try {
        const results = await storesApi.searchStores(q);
        setStoreSearchResults(results);
      } catch (err) {
        console.error('Store search failed', err);
        setStoreSearchResults([]);
      } finally {
        setIsSearchingStores(false);
      }
    }, 250);
    return () => {
      if (storeSearchTimer.current) {
        window.clearTimeout(storeSearchTimer.current);
      }
    };
  }, [storeSearch, selectedProject, selectedStore]);

  // Default drive-back start odo from end odo when the section becomes
  // applicable and the field is empty.
  useEffect(() => {
    if (isLastVisitOfDay && useCase === 'retroactive' && endOdo && !driveBackStartOdo) {
      setDriveBackStartOdo(endOdo);
    }
  }, [isLastVisitOfDay, useCase, endOdo, driveBackStartOdo]);

  // Default drive-back time start from time-out when section becomes applicable.
  useEffect(() => {
    if (isLastVisitOfDay && useCase === 'retroactive' && timeOut && !driveBackTimeStart) {
      setDriveBackTimeStart(timeOut);
    }
  }, [isLastVisitOfDay, useCase, timeOut, driveBackTimeStart]);

  // ==========================================================================
  // Cross-filter handlers
  // ==========================================================================

  const handleSelectStore = useCallback(async (store: StoreResult) => {
    setSelectedStore(store);
    setStoreSearch(`${store.retailer_abbreviation} #${store.legacystoreid}`);
    setShowStoreDropdown(false);
    setStoreSearchResults([]);
    setIsLoadingProjectsForStore(true);
    try {
      const projects = await storesApi.getProjectsByStore(store.id);
      const mapped: ProjectResult[] = projects.map((p) => ({
        projectid: p.projectid,
        projectdesc: (p.projectdesc && p.projectdesc.trim()) || (p.projectname && p.projectname.trim()) || `Project ${p.projectid}`,
      }));
      setProjectsForStore(mapped);
      // #2675 — a pinned admin project (from the Today "Always Available" tap)
      // stays selected regardless of the store's project list: these standing
      // projects (MISC/Admin/Training) aren't route-tied to every store, so we
      // must NOT clear or auto-override them when a store is chosen.
      const keepPinned = !!selectedProject && selectedProject.projectid === pinnedProjectId;
      if (keepPinned) {
        // leave the pinned project selected
      } else if (mapped.length === 1) {
        setSelectedProject(mapped[0]);
        setProjectSearch(`${mapped[0].projectid} - ${mapped[0].projectdesc}`);
      } else if (selectedProject && !mapped.some((p) => p.projectid === selectedProject.projectid)) {
        setSelectedProject(null);
        setProjectSearch('');
      }
    } catch (err) {
      console.error('Failed to load projects for store', err);
      setProjectsForStore([]);
    } finally {
      setIsLoadingProjectsForStore(false);
    }
  }, [selectedProject, pinnedProjectId]);

  const handleSelectProject = useCallback((project: ProjectResult) => {
    setSelectedProject(project);
    setProjectSearch(`${project.projectid} - ${project.projectdesc}`);
    setShowProjectDropdown(false);
  }, []);

  // ---- Filtered sources for the dropdowns ----
  const projectSourceList = useMemo<ProjectResult[]>(() => {
    return selectedStore ? projectsForStore : allProjects;
  }, [selectedStore, projectsForStore, allProjects]);

  const filteredProjects = useMemo<ProjectResult[]>(() => {
    if (!projectSearch.trim()) return projectSourceList;
    const q = projectSearch.toLowerCase().trim();
    return projectSourceList.filter((p) => {
      // projectdesc is always normalized to a string at load time, but use
      // String(...) defensively in case a future mapping change forgets.
      const desc = p.projectdesc === null || p.projectdesc === undefined ? '' : String(p.projectdesc);
      return String(p.projectid).includes(q) || desc.toLowerCase().includes(q);
    });
  }, [projectSourceList, projectSearch]);

  const storeSourceList = useMemo<StoreResult[]>(() => {
    return selectedProject ? projectStores : storeSearchResults;
  }, [selectedProject, projectStores, storeSearchResults]);

  const filteredStores = useMemo<StoreResult[]>(() => {
    if (!storeSearch.trim() || !selectedProject) return storeSourceList;
    const q = storeSearch.toLowerCase().trim();
    // Defensive coercion: getStoresByProject's response shape is loose —
    // storenumber comes back as number/null/string depending on the row.
    // Without `String(...)` we crash with "X.toLowerCase is not a function"
    // the moment the rep selects a project that has any rough row.
    const lower = (v: unknown): string =>
      v === null || v === undefined ? '' : String(v).toLowerCase();
    return storeSourceList.filter((s) => {
      return (
        lower(s.legacystoreid).includes(q) ||
        lower(s.name).includes(q) ||
        lower(s.retailer_abbreviation).includes(q) ||
        lower(s.storenumber).includes(q) ||
        lower(s.address).includes(q)
      );
    });
  }, [storeSourceList, storeSearch, selectedProject]);

  // ==========================================================================
  // Derived / validation
  // ==========================================================================

  const driveDurationLabel = useMemo<string | null>(() => {
    const a = timeToMinutes(driveTimeStart);
    const b = timeToMinutes(driveTimeEnd);
    if (a === null || b === null) return null;
    return formatDurationHM(b - a).text;
  }, [driveTimeStart, driveTimeEnd]);

  const driveMiles = useMemo<number | null>(() => {
    const a = parseInt(startOdo, 10);
    const b = parseInt(endOdo, 10);
    if (!isFinite(a) || !isFinite(b) || b < a) return null;
    return b - a;
  }, [startOdo, endOdo]);

  const storeDuration = useMemo<{ text: string; isOvernight: boolean } | null>(() => {
    const a = timeToMinutes(timeIn);
    const b = timeToMinutes(timeOut);
    if (a === null || b === null) return null;
    return formatDurationHM(b - a);
  }, [timeIn, timeOut]);

  const driveBackMiles = useMemo<number | null>(() => {
    const a = parseInt(driveBackStartOdo, 10);
    const b = parseInt(driveBackEndOdo, 10);
    if (!isFinite(a) || !isFinite(b) || b < a) return null;
    return b - a;
  }, [driveBackStartOdo, driveBackEndOdo]);

  const overlapResult = useMemo<OverlapResult>(() => {
    if (!timeline) return { conflict: null, message: null };
    const inMin = timeToMinutes(timeIn);
    const outMin = timeToMinutes(timeOut);
    if (inMin === null || outMin === null) return { conflict: null, message: null };
    if (outMin <= inMin) {
      return { conflict: null, message: 'Time out must be after time in.' };
    }
    // #2465: check against ALL timeline segments (drive / store_visit / drive_back),
    // not just store visits — the server rejects overlaps with any of them.
    return findTimelineOverlap(timeline.entries, inMin, outMin);
  }, [timeline, timeIn, timeOut]);

  // Validate gates for each step's Continue button.
  const step1Errors = useMemo<string[]>(() => {
    const errs: string[] = [];
    if (!date) errs.push('Date is required.');
    if (timelineLoading) errs.push('Waiting for timeline to load…');
    return errs;
  }, [date, timelineLoading]);

  const step2Errors = useMemo<string[]>(() => {
    const errs: string[] = [];
    if (!selectedProject) errs.push('Pick a project.');
    if (!selectedStore) errs.push('Pick a store.');
    return errs;
  }, [selectedProject, selectedStore]);

  const step3Errors = useMemo<string[]>(() => {
    const errs: string[] = [];
    if (!timeIn) errs.push('Time in is required.');
    if (!timeOut) errs.push('Time out is required.');
    if (!startOdo) errs.push('Drive-to start odometer is required.');
    if (!endOdo) errs.push('Drive-to end odometer is required.');
    const a = parseInt(startOdo, 10);
    const b = parseInt(endOdo, 10);
    if (startOdo && endOdo && (!isFinite(a) || !isFinite(b))) errs.push('Odometer values must be numbers.');
    if (isFinite(a) && isFinite(b) && b < a) errs.push('Drive end odometer cannot be less than start.');
    if (overlapResult.message) errs.push(overlapResult.message);
    if (reason === 'other' && !reasonOther.trim()) errs.push('Describe the reason in the free-form field.');
    // Drive-back validation only when the section is applicable.
    if (isLastVisitOfDay && useCase === 'retroactive') {
      const da = parseInt(driveBackStartOdo, 10);
      const db = parseInt(driveBackEndOdo, 10);
      if (driveBackStartOdo && driveBackEndOdo && (!isFinite(da) || !isFinite(db))) {
        errs.push('Drive-home odometer values must be numbers.');
      }
      if (isFinite(da) && isFinite(db) && db < da) {
        errs.push('Drive-home end odometer cannot be less than start.');
      }
    }
    return errs;
  }, [timeIn, timeOut, startOdo, endOdo, overlapResult, reason, reasonOther, isLastVisitOfDay, useCase, driveBackStartOdo, driveBackEndOdo]);

  // ==========================================================================
  // Submit
  // ==========================================================================

  const handleSubmit = useCallback(async () => {
    if (!selectedStore || !selectedProject) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const associateId = readAssociateId();
      if (!associateId) {
        setSubmitError('Unable to identify your associate account. Please log out and log back in.');
        setSubmitting(false);
        return;
      }

      const driveStartIso = driveTimeStart ? buildIsoTimestamp(date, driveTimeStart) : undefined;
      const driveEndIso = driveTimeEnd ? buildIsoTimestamp(date, driveTimeEnd) : undefined;

      const driveBackApplicable = isLastVisitOfDay && useCase === 'retroactive';
      const driveBackStartIso = driveBackApplicable && driveBackTimeStart ? buildIsoTimestamp(date, driveBackTimeStart) : undefined;
      const driveBackEndIso = driveBackApplicable && driveBackTimeEnd ? buildIsoTimestamp(date, driveBackTimeEnd) : undefined;
      const driveBackStartOdoNum = driveBackApplicable && driveBackStartOdo ? parseInt(driveBackStartOdo, 10) : undefined;
      const driveBackEndOdoNum = driveBackApplicable && driveBackEndOdo ? parseInt(driveBackEndOdo, 10) : undefined;

      const reasonObj = OFF_SCHEDULE_REASONS.find((r) => r.value === reason);
      const reasonLabel = reason === 'other' ? reasonOther.trim() : reasonObj?.label || '';

      // Queue-only manual visit (#2013). A dedicated op carries the FULL
      // payload — drive-to, return-drive, team-lead flags, and notes — that
      // the live checkIn op would silently drop. Reuse the stable
      // idempotencyKey as the visit localId so a double-tap/refresh dedups
      // (#1867) and StoreDetail can adopt the same visit. The sync engine
      // drains it (and resolves the server calllogid) on its own; we navigate
      // immediately on the localId. store_id is the legacystoreid (the
      // serializer's happy-path lookup), matching the live check-in path.
      const { enqueueOp } = await import('../sync/writeOps');
      const localId = await enqueueOp(
        'createManualVisit',
        {
          storeId: selectedStore.legacystoreid,
          projectId: selectedProject.projectid,
          associateId,
          storeName: `${selectedStore.retailer_abbreviation} #${selectedStore.legacystoreid}`,
          dteCall: date,
          stCallTime: buildIsoTimestamp(date, timeIn),
          driveToStartTime: driveStartIso,
          driveToEndTime: driveEndIso,
          driveOdoStart: parseInt(startOdo, 10),
          driveOdoEnd: parseInt(endOdo, 10),
          driveBackStartTime: driveBackStartIso,
          driveBackEndTime: driveBackEndIso,
          driveBackOdoStart: driveBackStartOdoNum,
          driveBackOdoEnd: driveBackEndOdoNum,
          teamProject: isTeamProject,
          teamLeader: isTeamProject ? isTeamLeader : false,
          notes: notes.trim() || undefined,
          offScheduleReason: reasonLabel || undefined,
        },
        idempotencyKey,
      );

      const stcalltimeIso = buildIsoTimestamp(date, timeIn);
      setManualVisit({
        date,
        useCase,
        timeIn: stcalltimeIso,
        timeOut: buildIsoTimestamp(date, timeOut),
        driveStartTime: driveStartIso ?? stcalltimeIso,
        driveEndTime: driveEndIso ?? stcalltimeIso,
        driveStartOdometer: parseInt(startOdo, 10),
        driveEndOdometer: parseInt(endOdo, 10),
        storeId: selectedStore.id,
        legacyStoreId: selectedStore.legacystoreid,
        storeName: `${selectedStore.retailer_abbreviation} #${selectedStore.legacystoreid}`,
        projectId: selectedProject.projectid,
        projectDescription: selectedProject.projectdesc,
        teamProject: isTeamProject,
        teamLeader: isTeamProject ? isTeamLeader : false,
        notes: notes.trim() || undefined,
        callLogId: null,
        visitLocalId: localId,
      });

      navigate(
        `/store/${selectedStore.legacystoreid}?projectId=${selectedProject.projectid}&visitId=${localId}`,
      );
    } catch (err) {
      // enqueueOp only fails on a local (IndexedDB) error — the server is
      // contacted asynchronously by the sync engine, so there is no API
      // response to parse here. Surface a generic message and let the rep
      // retry; server-side validation issues now surface via the queue's
      // Sync Issues page instead.
      console.error('Failed to queue manual entry:', err);
      setSubmitError(
        err instanceof Error
          ? `Could not save manual entry: ${err.message}`
          : 'Could not save manual entry. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [selectedStore, selectedProject, date, timeIn, timeOut, driveTimeStart, driveTimeEnd, startOdo, endOdo, isLastVisitOfDay, useCase, driveBackTimeStart, driveBackTimeEnd, driveBackStartOdo, driveBackEndOdo, isTeamProject, isTeamLeader, reason, reasonOther, notes, setManualVisit, navigate]);

  // ==========================================================================
  // Auth gate + render
  // ==========================================================================

  if (!user) {
    return <div className="p-4 text-center text-slate-500">Sign in required.</div>;
  }

  // Resume/discard prompt (#1867). If ManualVisitContext is already set when
  // the rep lands here (e.g. they pressed Back from StoreDetail, or the
  // PWA was reloaded mid-flow and sessionStorage rehydrated the context),
  // do NOT start a fresh form — they likely meant to resume the visit.
  // Offering Discard is the safety valve for "I changed my mind."
  if (existingManualVisit) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h2 className="mb-2 font-heading text-lg font-semibold text-amber-800">
            You have a manual entry in progress
          </h2>
          <p className="mb-4 text-sm text-amber-700">
            {new Date(existingManualVisit.date + 'T00:00:00').toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'short',
              day: 'numeric',
            })}
            {' · '}
            <span className="font-medium">{existingManualVisit.storeName}</span>
            {' · '}
            #{existingManualVisit.projectId}
            {existingManualVisit.projectDescription ? ` ${existingManualVisit.projectDescription}` : ''}
            {' · '}
            CallLog #{existingManualVisit.callLogId}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/store/${existingManualVisit.legacyStoreId}?projectId=${existingManualVisit.projectId}&callLogId=${existingManualVisit.callLogId}`,
                )
              }
              className="flex-1 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800"
            >
              Resume visit →
            </button>
            <button
              type="button"
              onClick={() => {
                clearManualVisit();
              }}
              className="flex-1 rounded-xl border-2 border-red-300 bg-white px-4 py-3 font-semibold text-red-700 transition-all hover:bg-red-50"
            >
              Discard &amp; start new
            </button>
          </div>
          <p className="mt-3 text-xs text-amber-600">
            Discarding drops the in-progress visit from this device. The CallLog stays on the server — your supervisor can still see it. Cancel from the visit page if you want to void it entirely.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      {/* Header + progress */}
      <header className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 pb-4 pt-6 text-white safe-area-inset-top">
        <button
          onClick={() => (step === 1 ? navigate(-1) : setStep((s) => (s - 1) as WizardStep))}
          className="mb-2 flex items-center gap-2 text-primary-100"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {step === 1 ? 'Back' : 'Previous step'}
        </button>
        <h1 className="font-heading text-xl font-bold">Manual Entry</h1>
        <p className="text-sm text-primary-100">
          Step {step} of 4: {
            step === 1 ? 'When' :
            step === 2 ? 'Where' :
            step === 3 ? 'Details' :
            'Confirm'
          }
        </p>

        {/* Progress bar */}
        <div className="mt-3 flex items-center gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-white' : 'bg-white/30'}`}
            />
          ))}
        </div>
      </header>

      <main className="space-y-4 p-4">
        {/* ===================== Step 1: When ===================== */}
        {step === 1 && (
          <>
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Date of visit
              </h2>
              <input
                type="date"
                value={date}
                max={today}
                onChange={(e) => {
                  setDate(e.target.value);
                  setTimeIn('');
                  setStartOdo('');
                  // Reset last-of-day toggle when switching to/from today
                  setIsLastVisitOfDay(false);
                }}
                className="block w-full rounded-md border border-slate-300 px-3 py-3 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <div className={`mt-3 rounded-lg p-3 text-sm ${useCase === 'live-takeover' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'}`}>
                <span className="font-medium">
                  {useCase === 'live-takeover' ? 'Live takeover' : 'Recording past visit'}
                </span>
                <span className="ml-1">
                  {useCase === 'live-takeover'
                    ? '— you\'re entering a visit for today. Drive-home will be captured when you end your day.'
                    : `— you're reconstructing a visit from ${new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}. If this was your last visit of that day you'll capture drive-home in the next step.`}
                </span>
              </div>
            </section>

            {/* Timeline preview */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Your timeline on {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </h2>
              {timelineLoading && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                  Loading timeline...
                </div>
              )}
              {timelineError && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{timelineError}</div>
              )}
              {!timelineLoading && !timelineError && timeline && timeline.entries.length === 0 && (
                <p className="text-sm text-slate-500">
                  No existing entries for this date. Your manual entry will be the first.
                </p>
              )}
              {!timelineLoading && !timelineError && timeline && timeline.entries.length > 0 && (
                <ul className="space-y-1.5">
                  {timeline.entries.map((e, idx) => (
                    <li
                      key={`${e.calllog_id}-${e.type}-${idx}`}
                      className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm ${
                        e.type === 'drive' || e.type === 'drive_back'
                          ? 'bg-slate-50 text-slate-700'
                          : 'bg-primary-50 text-primary-800'
                      }`}
                    >
                      <span className="font-mono text-xs">{formatTimeRange(e.start_time, e.end_time)}</span>
                      <span className="flex-1 truncate">
                        {e.type === 'store_visit' ? (
                          <>
                            {e.store_name}
                            {e.project_name && <span className="text-primary-600"> — {e.project_name}</span>}
                          </>
                        ) : (
                          e.context || (e.type === 'drive' ? 'Drive' : 'Drive home')
                        )}
                      </span>
                      {(e.start_odometer != null && e.end_odometer != null) && (
                        <span className="font-mono text-xs text-slate-500">{e.start_odometer} → {e.end_odometer}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <ContinueButton
              onClick={() => setStep(2)}
              disabled={step1Errors.length > 0}
              label="Continue: Where →"
              errors={step1Errors}
            />
          </>
        )}

        {/* ===================== Step 2: Where ===================== */}
        {step === 2 && (
          <>
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-1 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Store &amp; project
              </h2>
              <p className="mb-3 text-xs text-slate-500">
                Pick either first — the other narrows to matches.
              </p>

              {/* Project typeahead */}
              <div ref={projectDropdownRef} className="relative mb-4">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Project <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={projectSearch}
                    onChange={(e) => {
                      setProjectSearch(e.target.value);
                      if (selectedProject) setSelectedProject(null);
                      setShowProjectDropdown(true);
                      setProjectsRequested(true);
                    }}
                    onFocus={() => {
                      setShowProjectDropdown(true);
                      // Lazy-load the full project list on first use (#2117).
                      setProjectsRequested(true);
                    }}
                    placeholder={
                      isLoadingProjects
                        ? 'Loading projects…'
                        : isLoadingProjectsForStore
                          ? 'Loading projects for selected store…'
                          : selectedStore
                            ? `Pick from ${projectsForStore.length} project${projectsForStore.length === 1 ? '' : 's'} at this store`
                            : 'Type project # or name…'
                    }
                    disabled={isLoadingProjects || isLoadingProjectsForStore}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-10 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-100"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    {(isLoadingProjects || isLoadingProjectsForStore) ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                    ) : (
                      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {showProjectDropdown && filteredProjects.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {filteredProjects.slice(0, 50).map((p) => (
                      <button
                        key={p.projectid}
                        type="button"
                        onClick={() => handleSelectProject(p)}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left hover:bg-slate-50"
                      >
                        <span className="font-mono text-sm font-medium text-primary-600">{p.projectid}</span>
                        <span className="text-slate-600">-</span>
                        <span className="truncate text-sm text-slate-800">{p.projectdesc}</span>
                      </button>
                    ))}
                    {filteredProjects.length > 50 && (
                      <div className="px-4 py-2 text-center text-xs text-slate-500">
                        Type more to narrow results ({filteredProjects.length} total)
                      </div>
                    )}
                  </div>
                )}
                {showProjectDropdown && !isLoadingProjects && !isLoadingProjectsForStore && filteredProjects.length === 0 && projectSearch && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-center text-sm text-slate-500 shadow-lg">
                    No projects match "{projectSearch}"
                  </div>
                )}
              </div>

              {/* Store typeahead */}
              <div ref={storeDropdownRef} className="relative">
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Store <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={storeSearch}
                    onChange={(e) => {
                      setStoreSearch(e.target.value);
                      if (selectedStore) {
                        setSelectedStore(null);
                        setProjectsForStore([]);
                      }
                      setShowStoreDropdown(true);
                    }}
                    onFocus={() => setShowStoreDropdown(true)}
                    placeholder={
                      isLoadingStores
                        ? 'Loading project stores…'
                        : selectedProject
                          ? 'Type store # or name within this project…'
                          : 'Type a store # to search across all retailers…'
                    }
                    disabled={isLoadingStores}
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 pr-10 text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-slate-100"
                  />
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    {(isLoadingStores || isSearchingStores) ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-600 border-t-transparent"></div>
                    ) : (
                      <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </div>
                {showStoreDropdown && filteredStores.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    {filteredStores.slice(0, 50).map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => handleSelectStore(s)}
                        className="flex w-full flex-col px-4 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-primary-600">{s.legacystoreid}</span>
                          <span className="text-slate-600">-</span>
                          <span className="truncate text-sm font-medium text-slate-800">
                            {s.retailer_abbreviation ? `${s.retailer_abbreviation} #${s.legacystoreid}` : s.name || `Store ${s.legacystoreid}`}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 truncate">{s.address}</div>
                      </button>
                    ))}
                    {filteredStores.length > 50 && (
                      <div className="px-4 py-2 text-center text-xs text-slate-500">
                        Type more to narrow results ({filteredStores.length} total)
                      </div>
                    )}
                  </div>
                )}
                {showStoreDropdown && !isLoadingStores && !isSearchingStores && filteredStores.length === 0 && storeSearch && (
                  <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 bg-white p-3 text-center text-sm text-slate-500 shadow-lg">
                    No stores match "{storeSearch}"
                  </div>
                )}
              </div>
            </section>

            {/* Live off-schedule check-in (#3590 3a): the rep is standing at
                this store RIGHT NOW. That is a check-in, not a form — sending
                them on to type an end time for work still happening asserts a
                boundary nobody has observed. Hand off to StoreDetail's normal
                GPS check-in; checkout stamps the end when it actually happens.
                Only for TODAY: a takeover of an earlier hour still needs the
                asserted times below. */}
            {useCase === 'live-takeover' && selectedStore && selectedProject && step2Errors.length === 0 && (
              <div className="mb-3 rounded-xl border border-primary-200 bg-primary-50 p-4" data-testid="live-checkin-offer">
                <p className="text-sm font-medium text-slate-800">Are you at this store right now?</p>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as ReasonValue | '')}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  aria-label="Off-schedule reason"
                >
                  <option value="">No reason selected</option>
                  {OFF_SCHEDULE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {reason === 'other' && (
                  <input
                    type="text"
                    value={reasonOther}
                    onChange={(e) => setReasonOther(e.target.value)}
                    placeholder="Describe the reason"
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                )}
                <button
                  onClick={() => {
                    const reasonObj = OFF_SCHEDULE_REASONS.find((r) => r.value === reason);
                    const label = reason === 'other' ? reasonOther.trim() : reasonObj?.label || '';
                    const params = new URLSearchParams({ projectId: String(selectedProject.projectid) });
                    params.set('offScheduleReason', label);
                    if (selectedProject.projectdesc) params.set('projectDesc', selectedProject.projectdesc);
                    navigate(`/store/${selectedStore.legacystoreid}?${params.toString()}`);
                  }}
                  disabled={!reason || (reason === 'other' && !reasonOther.trim())}
                  className="mt-3 w-full rounded-xl bg-primary-600 py-3 font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  Check in now — I'm here
                </button>
                <p className="mt-2 text-xs text-slate-500">
                  {!reason || (reason === 'other' && !reasonOther.trim())
                    ? 'Pick the reason first — it is the record of why this visit exists.'
                    : 'Your visit starts when you check in; leaving the store records the end. No times to type.'}
                </p>
              </div>
            )}

            <ContinueButton
              onClick={() => setStep(3)}
              disabled={step2Errors.length > 0}
              label={useCase === 'live-takeover' ? 'Continue: Details — enter times myself →' : 'Continue: Details →'}
              errors={step2Errors}
            />
          </>
        )}

        {/* ===================== Step 3: Details ===================== */}
        {step === 3 && (
          <>
            {/* (1) Drive to Store — time + odometer grouped together */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">1</span>
                Drive to store
              </h2>
              {staleDriveNotice && (
                <p className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800">
                  You have a drive still running from{' '}
                  <strong>{dateToHHMM(new Date(staleDriveNotice.startTime))}</strong> with no miles on it,
                  so nothing was filled in from it. Enter this visit&rsquo;s times, then fix that drive on
                  the Time page.
                </p>
              )}
              {liveDrive && (
                <p className="mb-3 rounded-lg bg-blue-50 p-2 text-xs text-blue-800">
                  Filled in from the drive you are on right now &mdash; it started at{' '}
                  <strong>{dateToHHMM(new Date(liveDrive.startTime))}</strong>
                  {liveDrive.startOdometer != null && <> at <strong>{liveDrive.startOdometer}</strong> miles</>}
                  , and the arrival is set to now. Change anything that is not right.
                </p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-medium text-slate-700">
                  Left for store <span className="text-slate-400">(optional)</span>
                  <input
                    type="time"
                    value={driveTimeStart}
                    onChange={(e) => setDriveTimeStart(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="block text-xs font-medium text-slate-700">
                  Arrived at store <span className="text-slate-400">(optional)</span>
                  <input
                    type="time"
                    value={driveTimeEnd}
                    onChange={(e) => {
                      const arrived = e.target.value;
                      setDriveTimeEnd(arrived);
                      if (arrived && !timeIn) setTimeIn(arrived);
                    }}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="block text-xs font-medium text-slate-700">
                  Start odometer <span className="text-red-500">*</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={startOdo}
                    onChange={(e) => setStartOdo(e.target.value)}
                    placeholder="e.g. 12053"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="block text-xs font-medium text-slate-700">
                  End odometer <span className="text-red-500">*</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={endOdo}
                    onChange={(e) => setEndOdo(e.target.value)}
                    placeholder="e.g. 12068"
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
              </div>
              {(driveDurationLabel || driveMiles !== null) && (
                <div className="mt-2 flex gap-2 text-xs">
                  {driveDurationLabel && (
                    <span className="rounded-md bg-blue-50 px-2 py-1 text-blue-700">
                      Drive time: <span className="font-medium">{driveDurationLabel}</span>
                    </span>
                  )}
                  {driveMiles !== null && (
                    <span className="rounded-md bg-blue-50 px-2 py-1 text-blue-700">
                      Miles: <span className="font-medium">{driveMiles}</span>
                    </span>
                  )}
                </div>
              )}
              {startOdo && endOdo && parseInt(endOdo, 10) === parseInt(startOdo, 10) && parseInt(startOdo, 10) !== 0 && (
                <p className="mt-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
                  Start and end readings are the same — this records a 0-mile drive. Double-check the end reading if you did drive.
                </p>
              )}
            </section>

            {/* (2) Store Time */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-xs font-bold text-green-700">2</span>
                Store time
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs font-medium text-slate-700">
                  Time in <span className="text-red-500">*</span>
                  <input
                    type="time"
                    value={timeIn}
                    onChange={(e) => setTimeIn(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="block text-xs font-medium text-slate-700">
                  Time out <span className="text-red-500">*</span>
                  <input
                    type="time"
                    value={timeOut}
                    onChange={(e) => setTimeOut(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
              </div>
              {storeDuration && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="rounded-md bg-green-50 px-2 py-1 text-green-700">
                    Store time: <span className="font-medium">{storeDuration.text}</span>
                  </span>
                  {storeDuration.isOvernight && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">Overnight</span>
                  )}
                </div>
              )}
              {overlapResult.message && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                  ⚠ {overlapResult.message}
                </div>
              )}
            </section>

            {/* (3) Last visit of day? — explicit toggle */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-700">3</span>
                Last visit of the day?
              </h2>
              <p className="mb-3 text-xs text-slate-500">
                {useCase === 'live-takeover'
                  ? 'If yes, drive-home will still be captured automatically when you Clock Out on the Today page.'
                  : "If yes, you'll capture the drive home below since the day is already ended on the server."}
              </p>
              <YesNoToggle value={isLastVisitOfDay} onChange={setIsLastVisitOfDay} />

              {isLastVisitOfDay && useCase === 'retroactive' && (
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <h3 className="mb-3 font-heading text-sm font-medium text-slate-600">
                    Drive home from store
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block text-xs font-medium text-slate-700">
                      Left store
                      <input
                        type="time"
                        value={driveBackTimeStart}
                        onChange={(e) => setDriveBackTimeStart(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </label>
                    <label className="block text-xs font-medium text-slate-700">
                      Arrived home
                      <input
                        type="time"
                        value={driveBackTimeEnd}
                        onChange={(e) => setDriveBackTimeEnd(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </label>
                    <label className="block text-xs font-medium text-slate-700">
                      Drive-home start odo
                      <input
                        type="number"
                        inputMode="numeric"
                        value={driveBackStartOdo}
                        onChange={(e) => setDriveBackStartOdo(e.target.value)}
                        placeholder="defaults to end odo above"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </label>
                    <label className="block text-xs font-medium text-slate-700">
                      Drive-home end odo
                      <input
                        type="number"
                        inputMode="numeric"
                        value={driveBackEndOdo}
                        onChange={(e) => setDriveBackEndOdo(e.target.value)}
                        placeholder="odo at home"
                        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </label>
                  </div>
                  {driveBackMiles !== null && (
                    <p className="mt-2 rounded-md bg-blue-50 px-3 py-1.5 text-center text-xs text-blue-700">
                      Drive-home miles: <span className="font-medium">{driveBackMiles}</span>
                    </p>
                  )}
                  {driveBackStartOdo && driveBackEndOdo
                    && parseInt(driveBackEndOdo, 10) === parseInt(driveBackStartOdo, 10)
                    && parseInt(driveBackStartOdo, 10) !== 0 && (
                    <p className="mt-2 rounded-md bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
                      Start and end readings are the same — this records a 0-mile drive home. Double-check the end reading if you did drive.
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* (4) Team Project */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">4</span>
                Team project?
              </h2>
              <YesNoToggle
                value={isTeamProject}
                onChange={(v) => {
                  setIsTeamProject(v);
                  if (!v) setIsTeamLeader(false);
                }}
              />
              {isTeamProject && (
                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-slate-700">Are you the team leader?</label>
                  <YesNoToggle value={isTeamLeader} onChange={setIsTeamLeader} />
                </div>
              )}
            </section>

            {/* (5) Off-schedule reason + notes (both optional) */}
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 flex items-center gap-2 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">5</span>
                Reason &amp; notes
              </h2>
              <label className="mb-3 block text-sm font-medium text-slate-700">
                Reason this is off-schedule <span className="text-slate-400">(optional)</span>
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value as ReasonValue | '')}
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">No reason selected</option>
                  {OFF_SCHEDULE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </label>
              {reason === 'other' && (
                <input
                  type="text"
                  value={reasonOther}
                  onChange={(e) => setReasonOther(e.target.value)}
                  placeholder="Describe the reason…"
                  maxLength={120}
                  className="mb-3 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              )}
              <label className="block text-sm font-medium text-slate-700">
                Notes <span className="text-slate-400">(optional)</span>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Anything notable about this visit…"
                  className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
            </section>

            <ContinueButton
              onClick={() => setStep(4)}
              disabled={step3Errors.length > 0}
              label="Continue: Confirm →"
              errors={step3Errors}
            />
          </>
        )}

        {/* ===================== Step 4: Confirm ===================== */}
        {step === 4 && selectedStore && selectedProject && (
          <>
            <section className="rounded-xl bg-white p-4 shadow-card">
              <h2 className="mb-3 font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
                Visit summary
              </h2>
              <dl className="space-y-3 text-sm">
                <SummaryRow label="Date" value={new Date(date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} />
                <SummaryRow label="Mode" value={useCase === 'live-takeover' ? 'Live takeover (today)' : 'Recording past visit'} />
                <SummaryRow label="Store" value={`${selectedStore.retailer_abbreviation} #${selectedStore.legacystoreid}`} subValue={selectedStore.address} />
                <SummaryRow label="Project" value={`#${selectedProject.projectid} ${selectedProject.projectdesc}`} />
                <SummaryRow
                  label="Drive to store"
                  value={`${driveTimeStart || '—'} → ${driveTimeEnd || '—'}`}
                  subValue={`${startOdo} → ${endOdo} (${driveMiles ?? 0} mi)`}
                />
                <SummaryRow
                  label="Store time"
                  value={`${timeIn} → ${timeOut}`}
                  subValue={storeDuration?.text}
                />
                {isLastVisitOfDay && useCase === 'retroactive' && (
                  <SummaryRow
                    label="Drive home"
                    value={`${driveBackTimeStart || '—'} → ${driveBackTimeEnd || '—'}`}
                    subValue={driveBackStartOdo && driveBackEndOdo ? `${driveBackStartOdo} → ${driveBackEndOdo} (${driveBackMiles ?? 0} mi)` : undefined}
                  />
                )}
                <SummaryRow
                  label="Team"
                  value={isTeamProject ? (isTeamLeader ? 'Team project (leader)' : 'Team project') : 'Solo'}
                />
                {reason && (
                  <SummaryRow
                    label="Reason"
                    value={reason === 'other' ? reasonOther : OFF_SCHEDULE_REASONS.find((r) => r.value === reason)?.label || ''}
                  />
                )}
                {notes.trim() && <SummaryRow label="Notes" value={notes.trim()} />}
              </dl>
            </section>

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating entry…
                </>
              ) : (
                <>Continue to Visit Flow →</>
              )}
            </button>
          </>
        )}
      </main>
    </div>
  );
}

// ============================================================================
// Default export — wraps the wizard in an error boundary so render crashes
// surface as a readable message instead of an invisible white screen.
// ============================================================================

export default function ManualEntryPreflight() {
  return (
    <WizardErrorBoundary>
      <ManualEntryPreflightInner />
    </WizardErrorBoundary>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function ContinueButton({
  onClick,
  disabled,
  label,
  errors,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  errors: string[];
}) {
  return (
    <section className="rounded-xl bg-white p-4 shadow-card">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 px-4 py-3 font-semibold text-white shadow-lg transition-all hover:from-primary-700 hover:to-primary-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {label}
      </button>
      {errors.length > 0 && (
        <ul className="mt-3 list-disc space-y-0.5 pl-5 text-xs text-slate-500">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

function YesNoToggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
          !value ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
        }`}
      >
        No
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`flex-1 rounded-lg py-2.5 font-medium transition-colors ${
          value ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
        }`}
      >
        Yes
      </button>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  subValue,
}: {
  label: string;
  value: string;
  subValue?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-right">
        <div className="text-sm font-medium text-slate-800">{value}</div>
        {subValue && <div className="text-xs text-slate-500">{subValue}</div>}
      </dd>
    </div>
  );
}
